import { Link } from "react-router-dom";
import SiteHeader, { MobileSearchBar } from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { categories, resources } from "@/mocks/resources";

const positions = [
  {
    icon: "ri-file-list-3-line",
    title: "资源资讯聚合",
    text: "把分散的作品信息、版本变化与更新记录整理到统一的结构里，打开就能看到完整脉络。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选浏览",
    text: "类型、地区、年份、标签四个维度自由组合，配合三种排序方式，快速缩小范围。",
  },
  {
    icon: "ri-magic-line",
    title: "标签关联推荐",
    text: "详情页根据标签重合度推荐风格接近的作品，帮助你在一个主题里继续深入。",
  },
];

const steps = [
  { title: "选择分类", text: "从首页或顶部导航进入影视、动漫、音乐任意分类，先看清这一类的内容概况。" },
  { title: "组合筛选", text: "在左侧筛选栏选择类型、地区、年份或标签，列表会即时刷新为匹配结果。" },
  { title: "调整排序", text: "按时间最新、点击最多或评分优先排序，快速找到当下最合适的那一条。" },
  { title: "进入详情", text: "点击卡片查看剧情介绍、资源说明与相关标签，还能继续浏览同类推荐。" },
];

export default function AboutPage() {
  useDocumentMeta(
    "关于无码 - 高清资源资讯平台的定位与说明",
    "了解无码站点的定位、内容覆盖范围、使用说明、联系方式以及版权与免责声明。",
    "关于无码,无码平台介绍,高清资源资讯,联系方式",
  );

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 pb-24 md:pb-0">
        <div className="wrap pt-5 md:pt-7">
          <Breadcrumb items={[{ label: "关于" }]} />
        </div>

        <section className="wrap mt-4">
          <div className="relative overflow-hidden rounded-lg bg-primary-950">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20visual%20of%20layered%20navy%20blue%20geometric%20planes%20with%20warm%20amber%20light%20streaks%2C%20minimal%20architectural%20shapes%2C%20soft%20gradient%20depth%2C%20no%20text%2C%20high%20detail%2C%20premium%20media%20portal%20aesthetic&width=1600&height=700&seq=wm-about-hero&orientation=landscape"
                alt="无码平台视觉形象背景图"
                title="无码 高清资源资讯平台 视觉形象"
                className="w-full h-full object-cover object-center opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/40"></div>
            </div>
            <div className="relative z-10 px-5 py-10 md:px-12 md:py-16 max-w-3xl">
              <p className="chip bg-accent-500 text-background-50">关于无码</p>
              <h1 className="mt-4 font-heading font-extrabold text-2xl md:text-4xl text-background-50 leading-tight">
                一个把高清资源资讯整理清楚的内容门户
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-200 leading-8">
                无码面向对高清影视、动漫与音乐内容感兴趣的普通用户，提供分类浏览、条件筛选、关键词检索与标签推荐。我们不存放、不提供任何真实资源文件，只做信息层面的整理与导航。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
                >
                  开始浏览
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <a
                  href="mailto:hello@wuma-portal.example"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-primary-700 hover:border-accent-400 text-background-100 hover:text-accent-300 text-sm cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-mail-line" aria-hidden="true"></i>
                  联系我们
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="wrap mt-10 md:mt-14" aria-label="站点定位">
          <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">站点定位</h2>
          <p className="mt-2 text-sm text-foreground-600 leading-7 max-w-3xl">
            我们希望把「找内容」这件事变得透明：不用在多个页面之间来回跳，也能快速判断一条资源是否符合需求。
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
            {positions.map((item) => (
              <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
                <span className="w-11 h-11 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-7">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="wrap mt-10 md:mt-14" aria-label="内容覆盖范围">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 md:gap-8">
            <div>
              <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">内容覆盖范围</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-7">
                目前站点覆盖三大内容方向，共 {resources.length} 条虚构资源条目，每条都包含封面、简介、标签、评分与发布时间。
              </p>
              <div className="mt-5 space-y-3">
                {categories.map((category) => {
                  const count = resources.filter((item) => item.category === category.key).length;
                  const tags = Array.from(
                    new Set(resources.filter((item) => item.category === category.key).flatMap((item) => item.tags)),
                  );
                  return (
                    <div
                      key={category.key}
                      className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      <span className="w-11 h-11 rounded-md bg-accent-50 text-accent-700 flex items-center justify-center shrink-0">
                        <i className={`${category.icon} text-xl`} aria-hidden="true"></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading font-bold text-base text-foreground-950">{category.label}</h3>
                          <span className="chip bg-background-100 text-foreground-500">{count} 条资源</span>
                        </div>
                        <p className="mt-1.5 text-sm text-foreground-600 leading-6">{category.description}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {tags.slice(0, 5).map((tag) => (
                            <span key={tag} className="chip bg-secondary-100 text-secondary-900">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Link
                        to={category.path}
                        className="h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center justify-center hover:border-primary-300 hover:text-primary-600 cursor-pointer transition-colors whitespace-nowrap shrink-0"
                      >
                        查看分类
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>

            <aside className="bg-background-100 border border-background-200 rounded-lg p-5 self-start">
              <h3 className="font-heading font-bold text-sm text-foreground-950 flex items-center gap-2">
                <i className="ri-alert-line text-accent-600" aria-hidden="true"></i>
                数据说明
              </h3>
              <ul className="mt-3 space-y-2.5 text-xs text-foreground-600 leading-6">
                <li className="flex gap-2">
                  <i className="ri-check-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                  所有作品名称、评分与访问量均为虚构。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                  筛选与搜索结果由前端数据即时计算，不代表真实收录量。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                  相关推荐基于标签重合度生成，属演示性质。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line text-primary-500 mt-0.5" aria-hidden="true"></i>
                  站内不含任何下载链接或播放地址。
                </li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="wrap mt-10 md:mt-14" aria-label="使用说明">
          <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">使用说明</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {steps.map((step, index) => (
              <div key={step.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
                <span className="font-heading font-extrabold text-2xl text-accent-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 font-heading font-bold text-sm text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-xs text-foreground-600 leading-6">{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="wrap mt-10 md:mt-14" aria-label="联系方式与版权声明">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
              <h2 className="font-heading font-extrabold text-xl text-foreground-950">联系方式</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-7">
                内容纠错、合作咨询或功能建议，都可以通过以下方式找到我们。工作日内一般会在 48 小时内回复。
              </p>
              <ul className="mt-5 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">邮箱</p>
                    <a
                      href="mailto:hello@wuma-portal.example"
                      className="text-sm text-foreground-800 hover:text-primary-600 cursor-pointer transition-colors break-all"
                    >
                      hello@wuma-portal.example
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-accent-50 text-accent-700 flex items-center justify-center shrink-0">
                    <i className="ri-weibo-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">微博</p>
                    <a
                      href="https://51cgdaquan.asia"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-foreground-800 hover:text-primary-600 cursor-pointer transition-colors"
                    >
                      @无码资源资讯
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                    <i className="ri-wechat-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm text-foreground-800">wuma-media</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-primary-950 rounded-lg p-5 md:p-7 text-background-200">
              <h2 className="font-heading font-extrabold text-xl text-background-50">版权与免责声明</h2>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-background-300">
                <li className="flex gap-2">
                  <i className="ri-shield-check-line text-accent-400 mt-1" aria-hidden="true"></i>
                  本站所有作品名称、简介、评分与图片均为虚构演示内容，与任何真实作品、个人或机构无关。
                </li>
                <li className="flex gap-2">
                  <i className="ri-shield-check-line text-accent-400 mt-1" aria-hidden="true"></i>
                  本站不提供、不存储、不传播任何影视、动漫、音乐资源文件，也不提供下载或播放入口。
                </li>
                <li className="flex gap-2">
                  <i className="ri-shield-check-line text-accent-400 mt-1" aria-hidden="true"></i>
                  站内导航与分享按钮为界面演示，不会调用第三方平台接口。
                </li>
                <li className="flex gap-2">
                  <i className="ri-shield-check-line text-accent-400 mt-1" aria-hidden="true"></i>
                  如需反馈内容问题，请通过左侧邮箱联系，我们会尽快处理。
                </li>
              </ul>
              <Link
                to="/search"
                className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
              >
                去检索站内内容
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MobileSearchBar />
      <SiteFooter />
    </div>
  );
}